import React, {useState, useEffect} from 'react';
import useAsyncEffect from 'use-async-effect';
import {connect} from 'react-redux';
import {match} from 'react-router-dom';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {toast} from 'react-toastify';
import {Moment} from 'moment';

import {AppState} from '../Store';
import {Article, ArticleService} from '../Services/ArticleService';
import {ImageService} from '../Services/ImageService';
import {Author} from '../Services/AuthorService';
import {Page} from '../Models';
import {loadArticlesCreator} from '../Store/Admin/Actions';

import DateTime from 'react-datetime';
import {Link} from 'react-router-dom';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Markdown from '../Components/Markdown';
import Editor from './Editor';
import ArticlePreview from '../Components/ArticlePreview';
import LoadMore from '../Components/LoadMore';
import CategoryEditor from './CategoryEditor';
import ImageUpload from './ImageUpload';

type TParams = {
  id?: string
};

interface ArticlesProps {
  match: match<TParams>;

  articleService: ArticleService;
  authors: Author[];
  articles: Page<Article>;
  imageService: ImageService;

  setPage: (page: number) => Promise<void>;
}

function Articles({
  match,
  articleService,
  imageService,
  authors,
  articles,
  setPage,
}: ArticlesProps) {
  const [article, setArticle] = useState<Article | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const [authorOpen, setAuthorOpen] = useState(false);
  const toggleAuthor = () => setAuthorOpen(!authorOpen);

  useAsyncEffect(async isMounted => {
    const {params: {id}} = match;
    if (article === undefined && id !== undefined) {
      if (id === 'new') {
        if (authors.length === 0) {
          toast.error("Can't create an article with no authors set up");
          return;
        }
        const author = authors[0];
        setArticle({
          id: -1,
          title: '',
          body: '',
          date: new Date(),
          author,
          published: false,
          categories: [],
          image: -1,
          upvotes: 0,
          downvotes: 0,
        });
      }
      try {
        const res = await articleService.byId(Number(id));
        if (res.parsedBody !== undefined && isMounted()) {
          setArticle(res.parsedBody);
        }
      } catch (e) {
        console.log(e);
      }
    } else if (id === undefined) {
      setArticle(undefined);
    }
  }, [match]);

  useEffect(() => {
    if (articles.page === 1 && articles.items.length === 0) {
      setPage(1);
    }
  }, [articles, setPage]);

  const titleChanged = (e: React.FormEvent<HTMLInputElement>) => {
    if (article === undefined) {
      return;
    }
    setArticle({
      ...article,
      title: e.currentTarget.value,
    });
  };

  const dateChanged = (date: Moment | string) => {
    if (article === undefined || typeof date === 'string') {
      return;
    }

    setArticle({
      ...article,
      date: date.toDate(),
    });
  };

  const publishedChanged = (e: React.FormEvent<HTMLInputElement>) => {
    if (article === undefined) {
      return;
    }

    setArticle({
      ...article,
      published: e.currentTarget.checked,
    });
  };

  const bodyChanged = (content: string) => {
    if (article === undefined) {
      return;
    }

    setArticle({
      ...article,
      body: content,
    });
  };

  const authorChanged = (id: number) => {
    if (article === undefined) {
      return;
    }

    const author = authors.find(a => a.id === id);

    if (author === undefined) {
      return;
    }

    setArticle({
      ...article,
      author,
    });
  };

  const categoriesChanged = (categories: string[]) => {
    if (article === undefined) {
      return;
    }

    setArticle({
      ...article,
      categories,
    });
  };

  const imageChanged = (image: number) => {
    if (article === undefined) {
      return;
    }

    setArticle({
      ...article,
      image,
    });
  };

  const save = async () => {
    if (article === undefined) {
      toast.info("Honestly, I'm kinda impressed you managed to do that");
      return;
    }

    if (article.categories.length === 0) {
      toast.error("You must enter at least one category");
      return;
    }

    setSaving(true);

    if (article.id === -1) {
      try {
        const res = await articleService.create(article);
        setArticle(res.parsedBody);
        toast.success("Article created!");
      } catch {
        toast.error("Failed to save article");
      }
    } else {
      try {
        const res = await articleService.update(article);
        setArticle(res.parsedBody);
        toast.success("Article saved!");
      } catch {
        toast.error("Failed to save article");
      }
    }

    setSaving(false);
  };

  const back = () => {
    setArticle(undefined);
  };

  const {page, pageCount} = articles;

  if (article === undefined) {
    return (
      <div>
        <div className="post-content-area mb-100">
          <div className="world-category-area">
            <ul className="nav nav-tabs" role="tablist">
              <li className="title"><Link to="/admin/articles/new">New Article</Link></li>
            </ul>
            <div className="page-container">
              <div className="pages">
                <LoadMore digits={2} currentPage={page} pages={pageCount} callback={setPage} fragment="/admin/articles/" />
              </div>
            </div>
            <div className="tab-content">
              {articles.items.map(art => {
                return <ArticlePreview key={art.id} article={art} vertical={false} />;
              })}
            </div>
          </div>
        </div>
        <div className="page-container">
          <div className="pages">
            <LoadMore digits={2} currentPage={page} pages={pageCount} callback={setPage} fragment="/admin/articles/" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="post-content-area mb-100">
        <div className="world-category-area">
          <ul className="nav nav-tabs" role="tablist">
            <li className="title"><Link to="/admin/articles" onClick={back}>Back</Link></li>
          </ul>
        </div>
      </div>
      <div className="article-edit">
        <fieldset disabled={saving}>
          <div className="row">
            <label className="col-sm-2">ID</label>
            <div className="col-sm-10">
              {article.id}
            </div>
          </div>
          <div className="form-group row">
            <label className="col-sm-2">Title</label>
            <div className="col-sm-10">
              <input className="form-control" type="text" onChange={titleChanged} value={article.title} />
            </div>
          </div>
          <div className="form-group row">
            <label className="col-sm-2">Date</label>
            <div className="col-sm-10">
              <DateTime value={article.date} onChange={dateChanged} />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group row">
                <label className="col-sm-4">Published</label>
                <div className="col-sm-8">
                  <input type="checkbox" checked={article.published} onChange={publishedChanged} />
                </div>
              </div>
              <div  className="form-group row">
                <label className="col-sm-4">Author</label>
                <div className="col-sm-8">
                  <Dropdown isOpen={authorOpen} toggle={toggleAuthor}>
                    <DropdownToggle className="dropdown-toggle">{article.author.name}</DropdownToggle>
                    <DropdownMenu>
                      {authors.map(v => <DropdownItem key={v.id} onClick={() => authorChanged(v.id)}>{v.name}</DropdownItem>)}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
              <div className="form-group row">
                <label className="col-sm-4">Categories</label>
                <div className="col-sm-8">
                  <CategoryEditor categories={article.categories} onChange={categoriesChanged} />
                </div>
              </div>
            </div>
            <div className="col-sm-6">
              <ImageUpload id={article.image} onUpload={imageChanged} />
            </div>
          </div>
          <button className="btn btn-primary" type="submit" onClick={save}>Save</button>
          <div className="editor">
            <div className="row match">
              <div className="col-md-6">
                <Editor content={article.body} onChange={bodyChanged} imageService={imageService} />
              </div>
              <div className="col-md-6">
                <ArticlePreview article={article} vertical={false} />
                <div className="single-blog-content">
                  <div className="post-content">
                    <Markdown source={article.body} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  );
}

const mapStateToProps = (state: AppState) => {
  return {
    articleService: state.auth.articleService,
    authors: state.admin.authors.list,
    articles: state.admin.articles.list,
    imageService: state.auth.imageService,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    setPage: (page: number) => dispatch(loadArticlesCreator(page)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Articles);
